import * as React from 'react';
import { Badge, IconButton } from '@material-ui/core';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';
import AddIcon from '@material-ui/icons/Add';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import styled from 'styled-components';
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Input } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { getTaskAssignableUsersAsync, getTasksPendingSummaryAsync, updateTaskStatusAsync } from '../../../ajax/requests';
import TaskCompleteDialog from '../../../components/Tasks/TaskCompleteDialog';
import TaskCreateModal from '../../../components/Tasks/TaskCreateModal';
import { paths } from '../../constants';
import { TaskItemData, TaskItemStatus, UserData } from '../../../models';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { getMyUser } from '../../../store/session/selectors';
import {
    canCreateTasks,
    canUpdateTask,
    getTaskTypeIcon,
    getTaskTypeTranslationKey,
    hasExtendedTaskManagementScope,
    hasVisibleTaskManagementScope,
    sortTasksByDueDate,
    TaskDismissReasons,
} from '../../../pages/Tasks/taskUtils';
import { getTasksUpdatedAt } from '../../../store/general/selectors';
import { getEntitiesDataItems } from '../../../store/entities/selectors';
import { ClientEntity } from '../../../store/entities/state';

const StyledDropdownMenu = styled(DropdownMenu)`
    && {
        width: min(92vw, 34rem);
        max-height: 32rem;
        display: none;
        overflow: hidden;
        padding: var(--spacer-XS);
        border: 1px solid var(--surface-border-subtle);
        border-radius: 0.7rem;
        box-shadow: 0 12px 28px rgba(15, 34, 34, 0.16);
        background: var(--brand-accent);
    }

    &&.show {
        display: flex;
        flex-direction: column;
    }
`;

const TaskMenuBody = styled.div`
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding-right: var(--spacer-XXS);
`;

const TaskMenuFooter = styled.div`
    flex-shrink: 0;
    margin-top: var(--spacer-XS);
    padding-top: var(--spacer-XS);
    border-top: 1px solid var(--surface-border-subtle);
`;

const TaskRow = styled(DropdownItem)`
    && {
        white-space: normal;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: var(--spacer-S);
        padding: var(--spacer-S);
        margin-bottom: var(--spacer-XXS);
        border-radius: 0.6rem;
        border: 1px solid transparent;
        transition: background-color 0.15s ease, border-color 0.15s ease;

        &:hover,
        &:focus {
            background-color: var(--surface-brand-soft);
            border-color: var(--surface-border-subtle);
        }
    }
`;

const TaskIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.9rem;
    height: 1.9rem;
    border-radius: 999px;
    flex-shrink: 0;
    color: var(--brand-secondary);
    background: var(--surface-brand-soft);
`;

const TaskSummaryButton = styled(Button)`
    && {
        border: 0;
        background: none;
        text-align: left;
        color: inherit;
        padding: 0;
        flex: 1;
        min-width: 0;
    }
`;

const TaskDescription = styled.div`
    font-size: 0.85rem;
    color: var(--text-strong);
    line-height: 1.35;
    overflow-wrap: anywhere;
`;

const TaskMeta = styled.div`
    margin-bottom: 0.2rem;
    font-size: 0.75rem;
    color: var(--text-muted);
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
`;

const TaskTypeBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 0.12rem 0.45rem;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.01em;
    line-height: 1.2;
    color: var(--brand-secondary);
    background: var(--surface-brand-soft);
`;

const TaskAssignee = styled.span`
    font-size: 0.76rem;
    font-weight: 600;
    color: var(--text-muted);
`;

const TaskDescriptionEmphasis = styled.span`
    font-weight: 600;
`;

const TaskActions = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-XXS);
`;

const TaskActionButton = styled(IconButton)`
    && {
        padding: 0.25rem;
        color: var(--text-muted);
    }
`;

const TaskSectionHeader = styled.div`
    padding: var(--spacer-XS) var(--spacer-S);
    color: var(--text-muted);
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
`;

const TaskSectionDivider = styled(DropdownItem)`
    && {
        margin: var(--spacer-XXS) 0;
        border-top: 1px solid var(--surface-border-subtle);
    }
`;

const TaskEmptyState = styled(DropdownItem)`
    && {
        color: var(--text-muted);
        padding: var(--spacer-S);
    }
`;

const SeeAllTasksRow = styled(DropdownItem)`
    && {
        border-radius: 0.6rem;
        padding: var(--spacer-S);
    }
`;

const SeeAllTasksContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacer-S);
`;

const SeeAllTasksButton = styled(Button)`
    && {
        border: 0;
        background: none;
        padding: 0;
        color: var(--brand-secondary);
        font-weight: 700;
        text-align: center;
        flex: 1;
        text-decoration: none;
    }
`;

const AddTaskButton = styled(IconButton)`
    && {
        padding: 0.25rem;
        color: var(--text-muted);
    }
`;

const TaskDetailsBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
`;

const TaskDetailsActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: var(--spacer-XS);
`;

const TasksMenu: React.FC = () => {
    const t = useTranslations();
    const history = useHistory();
    const user = useSelector(getMyUser);
    const tasksUpdatedAt = useSelector(getTasksUpdatedAt);
    const clients = useSelector(getEntitiesDataItems<ClientEntity>('clients'));

    const [isOpen, setOpen] = React.useState(false);
    const [tasks, setTasks] = React.useState<TaskItemData[]>([]);
    const [users, setUsers] = React.useState<UserData[]>([]);
    const [selectedTask, setSelectedTask] = React.useState<TaskItemData | null>(null);
    const [completeTask, setCompleteTask] = React.useState<TaskItemData | null>(null);
    const [detailsNotes, setDetailsNotes] = React.useState('');
    const [showCreate, setShowCreate] = React.useState(false);
    const [isLoading, setLoading] = React.useState(false);
    const canCreateTaskItems = React.useMemo(() => canCreateTasks(user), [user?.isClient, !!user]);

    const loadSummary = React.useCallback(async () => {
        try {
            const summary = await getTasksPendingSummaryAsync();
            setTasks(sortTasksByDueDate(summary.tasks));
        } catch {
            setTasks([]);
        }
    }, []);

    React.useEffect(() => {
        loadSummary();
    }, [loadSummary, tasksUpdatedAt]);

    React.useEffect(() => {
        if (!canCreateTaskItems) {
            setUsers([]);
            return;
        }

        getTaskAssignableUsersAsync()
            .then(scopeUsers => {
                if (scopeUsers.length > 0) {
                    setUsers(scopeUsers);
                    return;
                }

                setUsers(user ? [user] : []);
            })
            .catch(() => setUsers(user ? [user] : []));
    }, [canCreateTaskItems, user?.isClient]);

    const hasScopedManagementAccess = React.useMemo(() => hasVisibleTaskManagementScope(tasks, user), [tasks, user]);
    const hasExtendedScopedManagementAccess = React.useMemo(() => hasExtendedTaskManagementScope(user, users), [user, users]);

    const groupedTasks = React.useMemo(() => {
        const today = moment().startOf('day');
        const tomorrow = today.clone().add(1, 'day');

        return {
            overdue: tasks.filter(task => moment(task.dueDate).isBefore(today)),
            todayAndTomorrow: tasks.filter(task => (
                moment(task.dueDate).isSame(today, 'day')
                || moment(task.dueDate).isSame(tomorrow, 'day')
            )),
        };
    }, [tasks]);

    const count = groupedTasks.overdue.length + groupedTasks.todayAndTomorrow.length;
    const todayAndTomorrowLabel = t(TK.today).trim().toLowerCase() === 'hoje'
        ? 'Hoje & Amanhã'
        : 'Today & Tomorrow';

    const updateTask = React.useCallback(async (task: TaskItemData, status: TaskItemStatus, dismissReason?: string, notes?: string) => {
        if (!canUpdateTask(task, user, hasScopedManagementAccess)) return;

        setLoading(true);
        try {
            await updateTaskStatusAsync(task.id, {
                status,
                dismissReason,
                notes,
            });
            await loadSummary();
        } finally {
            setLoading(false);
        }
    }, [hasScopedManagementAccess, loadSummary, user]);

    const openDetails = (task: TaskItemData) => {
        setSelectedTask(task);
        setDetailsNotes(task.notes ?? '');
    };

    const renderTaskRow = (task: TaskItemData) => {
        const canAct = canUpdateTask(task, user, hasScopedManagementAccess);
        const TypeIcon = getTaskTypeIcon(task.type);

        return (
            <TaskRow
                key={task.id}
                tag="div"
            >
                <TaskIcon>
                    <TypeIcon fontSize="small" />
                </TaskIcon>
                <TaskSummaryButton
                    color="link"
                    onClick={() => openDetails(task)}
                >
                    <TaskMeta>
                        <TaskTypeBadge>{t(getTaskTypeTranslationKey(task.type))}</TaskTypeBadge>
                        <TaskAssignee>{task.assignedUserName || t(TK.none)}</TaskAssignee>
                    </TaskMeta>
                    <TaskDescription>
                        <TaskDescriptionEmphasis>{task.clientName || t(TK.client)}</TaskDescriptionEmphasis>
                        {task.title ? ` - ${task.title}` : ''}
                    </TaskDescription>
                </TaskSummaryButton>
                {canAct && (
                    <TaskActions>
                        <TaskActionButton size="small" disabled={isLoading} onClick={() => setCompleteTask(task)}>
                            <CheckIcon fontSize="small" />
                        </TaskActionButton>
                        <TaskActionButton size="small" disabled={isLoading} onClick={() => updateTask(task, TaskItemStatus.Dismissed, TaskDismissReasons.notApplicable)}>
                            <ClearIcon fontSize="small" />
                        </TaskActionButton>
                    </TaskActions>
                )}
            </TaskRow>
        );
    };

    return (
        <>
            <Dropdown className="notificationsMenuContainer" isOpen={isOpen} toggle={() => setOpen(!isOpen)}>
                <DropdownToggle nav className="notificationsButton">
                <IconButton>
                    <Badge badgeContent={count} color="secondary" invisible={count === 0}>
                        <AssignmentTurnedInIcon />
                    </Badge>
                </IconButton>
            </DropdownToggle>
            <StyledDropdownMenu right>
                <TaskMenuBody>
                    <TaskSectionHeader>{t(TK.dueOverdue)}</TaskSectionHeader>
                    {groupedTasks.overdue.length ? groupedTasks.overdue.map(renderTaskRow) : <TaskEmptyState disabled>{t(TK.noTasksFound)}</TaskEmptyState>}
                    <TaskSectionDivider divider />
                    <TaskSectionHeader>{todayAndTomorrowLabel}</TaskSectionHeader>
                    {groupedTasks.todayAndTomorrow.length ? groupedTasks.todayAndTomorrow.map(renderTaskRow) : <TaskEmptyState disabled>{t(TK.noTasksFound)}</TaskEmptyState>}
                </TaskMenuBody>
                <TaskMenuFooter>
                    <SeeAllTasksRow tag="div">
                        <SeeAllTasksContent>
                            <SeeAllTasksButton color="link" onClick={() => history.push(paths.tasks())}>
                                {t(TK.seeAllTasks)}
                            </SeeAllTasksButton>
                            {canCreateTaskItems && (
                                <AddTaskButton size="small" onClick={() => setShowCreate(true)}>
                                    <AddIcon fontSize="small" />
                                </AddTaskButton>
                            )}
                        </SeeAllTasksContent>
                    </SeeAllTasksRow>
                </TaskMenuFooter>
            </StyledDropdownMenu>
        </Dropdown>

            <Dialog open={selectedTask != null} onClose={() => setSelectedTask(null)} fullWidth maxWidth="sm">
                <DialogTitle>{t(TK.taskDetails)}</DialogTitle>
                <DialogContent>
                    <TaskDetailsBody>
                        <div><b>{selectedTask?.clientName || '-'}</b></div>
                        <div>{selectedTask?.title || '-'}</div>
                        <Input
                            type="textarea"
                            rows={4}
                            value={detailsNotes}
                            onChange={event => setDetailsNotes(event.target.value)}
                            placeholder={t(TK.notes)}
                        />
                        <TaskDetailsActions>
                            <Button color="light" onClick={() => setSelectedTask(null)}>
                                {t(TK.cancel)}
                            </Button>
                            <Button
                                color="danger"
                                disabled={!selectedTask || isLoading || !canUpdateTask(selectedTask, user, hasScopedManagementAccess)}
                                onClick={async () => {
                                    if (!selectedTask) return;
                                    await updateTask(selectedTask, TaskItemStatus.Dismissed, TaskDismissReasons.notApplicable, detailsNotes);
                                    setSelectedTask(null);
                                }}
                            >
                                {t(TK.dismissTask)}
                            </Button>
                            <Button
                                color="success"
                                disabled={!selectedTask || isLoading || !canUpdateTask(selectedTask, user, hasScopedManagementAccess)}
                                onClick={async () => {
                                    if (!selectedTask) return;
                                    setCompleteTask({
                                        ...selectedTask,
                                        notes: detailsNotes,
                                    });
                                    setSelectedTask(null);
                                }}
                            >
                                {t(TK.completeTask)}
                            </Button>
                        </TaskDetailsActions>
                    </TaskDetailsBody>
                </DialogContent>
            </Dialog>
            <TaskCompleteDialog
                task={completeTask}
                onClose={() => setCompleteTask(null)}
                onCompleted={loadSummary}
            />
            <TaskCreateModal
                show={showCreate}
                onClose={() => setShowCreate(false)}
                onCreated={loadSummary}
                initialAssignedUserId={!hasExtendedScopedManagementAccess ? user?.id : undefined}
                allowAssignedUserSelection={hasExtendedScopedManagementAccess && users.length > 1}
                users={users}
                clients={clients}
            />
        </>
    );
};

export default TasksMenu;
